import { useCallback, useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Badge,
  useToast,
  Text,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCandidateDataasync,
  selectCandidateData,
  selectPagination,
  verifyCandidateAsync,
} from "../../CandidateData/CandidateDataSlice";
import { TimeDifference } from "../../../common/TimeDifference";
import { TimeDifferenceadmin } from "../../../common/TimeDifferenceadmin";
import { exportCandidatesToExcel } from "../../CandidateData/exportCandidates";

const CandidateList = () => {
  const dispatch = useDispatch();
  const candidatedata = useSelector(selectCandidateData);
  const pagination = useSelector(selectPagination);
  const toast = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Fetches candidates from the admin `fetch-candidate-list` endpoint.
  const getCandidates = useCallback(
    (page = currentPage) => {
      setIsLoading(true);
      return dispatch(fetchCandidateDataasync(page))
        .unwrap()
        .then(() => setIsLoading(false))
        .catch((error) => {
          setIsLoading(false);
          toast({
            title: "Could not fetch candidates",
            description:
              typeof error === "string"
                ? error
                : "There was an error fetching the candidate list.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        });
    },
    [dispatch, currentPage, toast]
  );

  useEffect(() => {
    getCandidates(currentPage);
  }, [getCandidates, currentPage]);

  const handleExportExcel = () => {
    if (!candidatedata || candidatedata.length === 0) {
      toast({
        title: "Nothing to export",
        description: "Click \"Get Candidates\" to load candidates first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }
    exportCandidatesToExcel(candidatedata);
    toast({
      title: "Export started",
      description: `Exporting ${candidatedata.length} candidate(s) to Excel.`,
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top-right",
    });
  };

  const handlePrevPage = () => {
    if (pagination.current_page > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.current_page < pagination.last_page) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleVerifyClick = (candidate) => {
    setSelectedCandidate(candidate);
    onOpen();
  };

  const handleVerifyConfirm = () => {
    if (selectedCandidate) {
      dispatch(
        verifyCandidateAsync({
          uuid: selectedCandidate.uuid,
          verifyData: { status: true },
        })
      )
        .unwrap()
        .then(() => {
          onClose();
          toast({
            title: "Candidate Verified",
            description: "The candidate has been successfully verified.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          dispatch(fetchCandidateDataasync(currentPage));
        })
        .catch((error) => {
          console.error("Verification failed:", error);
          toast({
            title: "Verification Failed",
            description: "There was an error verifying the candidate.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          onClose();
        });
    }
  };

  const handleNotVerify = () => {
    if (selectedCandidate) {
      dispatch(
        verifyCandidateAsync({
          uuid: selectedCandidate.uuid,
          verifyData: { status: false },
        })
      )
        .unwrap()
        .then(() => {
          onClose();
          toast({
            title: "Candidate Not Verified",
            description: "The candidate has not been verified.",
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          dispatch(fetchCandidateDataasync(currentPage));
        })
        .catch((error) => {
          console.error("Verification failed:", error);
          toast({
            title: "Verification Failed",
            description: "There was an error verifying the candidate.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
          onClose();
        });
    }
  };

  // Formats "X yr Y mo" from experience_years / experience_months (both may be
  // null or 0). Returns "-" when neither is provided.
  const formatExperience = (data) => {
    const years = data?.experience_years;
    const months = data?.experience_months;
    const hasYears = years !== null && years !== undefined && years !== "";
    const hasMonths = months !== null && months !== undefined && months !== "";
    if (!hasYears && !hasMonths) return "-";
    const parts = [];
    if (hasYears) parts.push(`${years} yr`);
    if (hasMonths) parts.push(`${months} mo`);
    return parts.join(" ");
  };

  // Skeleton loading component for table rows
  const SkeletonRow = () => (
    <Tr>
      <Td><Skeleton height="20px" width="20px" /></Td>
      <Td><Skeleton height="20px" width="120px" /></Td>
      <Td><Skeleton height="20px" width="150px" /></Td>
      <Td><Skeleton height="20px" width="100px" /></Td>
      <Td><Skeleton height="20px" width="70px" /></Td>
      <Td><Skeleton height="20px" width="80px" /></Td>
      <Td><Skeleton height="20px" width="90px" /></Td>
      <Td><Skeleton height="20px" width="100px" /></Td>
      <Td><Skeleton height="20px" width="100px" /></Td>
    </Tr>
  );

  return (
    <div className="contentAdmin bg-adminbg rounded p-4">
      <div
        className="bg-white my-5 rounded-md"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="flex justify-between items-center">
          <p className="text-xl text-black font-semibold px-3 py-7">
            Candidates List
          </p>
          <div className="flex items-center gap-2 px-3">
            <Button
              colorScheme="blue"
              size="sm"
              isLoading={isLoading}
              loadingText="Fetching"
              onClick={() => getCandidates(currentPage)}
            >
              Get Candidates
            </Button>
            <Button
              colorScheme="green"
              size="sm"
              variant="outline"
              isDisabled={!candidatedata || candidatedata.length === 0}
              onClick={handleExportExcel}
            >
              Export to Excel
            </Button>
          </div>
        </div>
        <TableContainer>
          <Table size="sm">
            <Thead className="bg-gray-200">
              <Tr>
                <Th>ID</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Gender</Th>
                <Th>Experience</Th>
                <Th>Work Status</Th>
                <Th>Current City</Th>
                <Th>Job Title</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                // Show skeleton rows while loading
                Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonRow key={index} />
                ))
              ) : candidatedata && candidatedata.length > 0 ? (
                candidatedata.map((data, index) => (
                  <Tr key={data.uuid || data.email || index}>
                    <Td>{index + 1}</Td>
                    <Td>{data.candidate_name || data.name || "-"}</Td>
                    <Td textColor={"green"}>{data.email || "-"}</Td>
                    <Td>
                      <div className="text-sm opacity-90 text-blue-700">
                        {data.phone || "-"}
                      </div>
                    </Td>
                    <Td>{data.gender || "-"}</Td>
                    <Td>{formatExperience(data)}</Td>
                    <Td>{data.work_status || "-"}</Td>
                    <Td>{data.current_city || "-"}</Td>
                    <Td>{data.job_title || "-"}</Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan="9" className="text-center py-4">
                    No data available
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </div>

      {/* Pagination with skeleton loading */}
      <div className="flex justify-center my-2">
        {isLoading ? (
          <div className="flex items-center gap-4">
            <Skeleton height="40px" width="80px" />
            <Skeleton height="20px" width="120px" />
            <Skeleton height="40px" width="80px" />
          </div>
        ) : (
          <>
            <Button
              onClick={handlePrevPage}
              isDisabled={pagination.current_page === 1}
              colorScheme="blue"
              mx={2}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-lg font-medium">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
            <Button
              onClick={handleNextPage}
              isDisabled={pagination.current_page === pagination.last_page}
              colorScheme="blue"
              mx={2}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Next
            </Button>
          </>
        )}
      </div>

      {/* Verification Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent maxW="2xl" borderradius="lg" boxShadow="lg">
          <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
            Verify Candidate
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Text fontSize="md" textAlign="center">
              Do you want to verify this candidate{" "}
              <Text as="span" fontWeight="semibold" color="red.500">
                "{selectedCandidate?.name}"
              </Text>
              ?
            </Text>
          </ModalBody>
          <ModalFooter display="flex" gap="2" justifyContent="center">
            <Button colorScheme="blue" w="40" onClick={handleVerifyConfirm}>
              Yes
            </Button>
            <Button
              variant="outline"
              colorScheme="blue"
              w="40"
              onClick={handleNotVerify}
            >
              No
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CandidateList;